import React, { useEffect, useRef, useState } from "react";
import MetisMenu from "metismenujs";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  changeLayout,
  changeLayoutWidth,
  changeSidebarTheme,
  changeSidebarType,
  changePreloader,
} from "../../store/actions";
import ProfileMenu from "components/CommonForBoth/TopbarDropdown/ProfileMenu";

// Role-based sidebar menu config
const getMenuItems = (role, t) => [
  { type: "title", label: t("Menu") },
  {
    label: t("Dashboard"),
    icon: "ri-dashboard-line",
    to: "/",
    badge: { text: "3", class: "bg-success" },
  },
  {
    label: t("Calendar"),
    icon: "ri-calendar-2-line",
    to: "/calendar",
  },
  {
    label: t("Profile"),
    icon: "mdi mdi-account-circle",
    to: "/profile",
  },
  { type: "title", label: t("Management") },
  ...(role !== 3
    ? [
        {
          label: t("Orders"),
          icon: "mdi mdi-fuel",
          to: "/orders",
          children: [
            { label: t("View Orders"), to: "/orders" },
            { label: t("Add Order"), to: "/add-order" },
          ],
        },
      ]
    : []),
  {
    label: t("Customers"),
    icon: "mdi mdi-account",
    to: "/customers",
    children: [
      { label: t("View Customers"), to: "/customers" },
      ...(role === 1
        ? [{ label: t("Add Customer"), to: "/add-customer" }]
        : []),
    ],
  },
  ...(role === 3) ? [
      {
      label: t("Orders"),
      icon: "mdi mdi-fuel",
      to: "/orders",
      },
     {
        label: t("Reports"),
        icon: "mdi mdi-book-open",
        to: "/reports",
        children: [
          { label: t("due date reports"), to: "/reports" },
          { label: t("Ledger reports"), to: "/ledger-report" },
          { label: t("Full reports"), to: "/full-report" },
          { label: t("Transaction reports"), to: "/transaction-report" },
        ],
    },
    {
      label: t("Transactions"),
      icon: "mdi mdi-bank",
      to: "/transactions",
      children: [
        { label: t("View Transactions"), to: "/transactions" },
        { label: t("Add Transaction"), to: "/add-transaction" },
      ],
    },
     {
        label: t("Stock Entry"),
        icon: "mdi mdi-history",
        to: "/stock-history",
      },
] : [],
  ...(role === 1
    ? [
        {
          label: t("Filler"),
          icon: "ri-contacts-book-line",
          to: "/fillers",
          children: [
            { label: t("View Filler"), to: "/fillers" },
            { label: t("Add Filler"), to: "/add-filler" },
          ],
        },
        {
          label: t("Transactions"),
          icon: "mdi mdi-bank",
          to: "/transactions",
          children: [
            { label: t("View Transactions"), to: "/transactions" },
            { label: t("Add Transaction"), to: "/add-transaction" },
          ],
        },
        {
          label: t("Nozzle"),
          icon: "mdi mdi-truck-fast",
          to: "/nozzles",
          children: [
            { label: t("View Nozzle"), to: "/nozzles" },
            { label: t("Add Nozzle"), to: "/add-nozzle" },
          ],
        },
        {
          label: t("Users"),
          icon: "mdi mdi-account-multiple",
          to: "/users",
          children: [
            { label: t("View Users"), to: "/users" },
            { label: t("Add User"), to: "/add-user" },
          ],
        },
        {
          label: t("Reports"),
          icon: "mdi mdi-book-open",
          to: "/reports",
          children: [
            { label: t("due date reports"), to: "/reports" },
            { label: t("Ledger reports"), to: "/ledger-report" },
            { label: t("Full reports"), to: "/full-report" },
            { label: t("Profit reports"), to: "/profit-report" }, 
            { label: t("Transaction reports"), to: "/transaction-report" },
            { label: t("Billing History"), to: "/billing-history" },
            { label: t("Create Billing"), to: "/create-billing" },
          ],
        },
        {
          label: t("Price"),
          icon: "mdi mdi-currency-inr",
          to: "/prices",
        },
        {
          label: t("Accountant History"),
          icon: "mdi mdi-history",
          to: "/accountant-history",
        },       
        {
          label: t("Stock Entry"),
          icon: "mdi mdi-history",
          to: "/stock-history",
        },
      ]
    : []),
];

const SidebarContent = (props) => {
  const [role, setRole] = useState(null);
  const location = useLocation();
  const { t } = useTranslation();
  const menuRef = useRef(null);

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    setRole(authUser?.user?.role ?? null);
  }, []);

  // 🔐 Initialize MetisMenu only after role is set and DOM is ready
  useEffect(() => {
    if (role !== null && menuRef.current) {
      const menu = new MetisMenu("#side-menu");
      return () => menu.dispose?.();
    }
  }, [role]);

  const isPathActive = (path) => location.pathname === path;

  return (
    <React.Fragment>
      <div id="sidebar-menu">
        <ul className="metismenu list-unstyled" id="side-menu" ref={menuRef}>
          {getMenuItems(role, t).map((item, index) => {
            if (item.type === "title") {
              return (
                <li className="menu-title" key={index}>
                  {item.label}
                </li>
              );
            }

            const isActive = isPathActive(item.to);

            return (
              <li key={index}>
                <Link
                  to={item.to}
                  className={`waves-effect ${
                    item.children ? "has-arrow" : ""
                  } ${isActive ? "active" : ""}`}
                >
                  <i className={item.icon}></i>
                  {item.badge && (
                    <span
                      className={`badge rounded-pill float-end ${item.badge.class}`}
                    >
                      {item.badge.text}
                    </span>
                  )}
                  <span className="ms-1">{item.label}</span>
                </Link>

                {item.children && (
                  <ul className="sub-menu">
                    {item.children.map((child, childIdx) => (
                      <li
                        key={childIdx}
                        className={isPathActive(child.to) ? "mm-active" : ""}
                      >
                        <Link
                          to={child.to}
                          className={isPathActive(child.to) ? "active" : ""}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}

          {/* Logout/Profile */}
          <li className="logout-btns">
            <ProfileMenu />
          </li>
        </ul>
      </div>
    </React.Fragment>
  );
};

const mapStatetoProps = (state) => ({
  ...state.Layout,
});

export default connect(mapStatetoProps, {
  changeLayout,
  changeSidebarTheme,
  changeSidebarType,
  changeLayoutWidth,
  changePreloader,
})(SidebarContent);
